import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Confirmation } from 'freemium-ui';
import moment from 'moment';

import AnnouncementLogo from 'images/announcement.svg';
import { ConfirmationDialogContent, StyledFsMigrationBanner } from './style';
import request, { pushToast } from 'helpers/request';

const MIGRATION_LINKS = [
  {
    title: 'components.fsBanner.bannerModal.migration.withFreshservice.title',
    content:
      'components.fsBanner.bannerModal.migration.withFreshservice.description',
    url: 'https://www.freshworks.com/freshservice/migrate-freshstatus-to-existing-freshservice/'
  },
  {
    title:
      'components.fsBanner.bannerModal.migration.withoutFreshservice.title',
    content:
      'components.fsBanner.bannerModal.migration.withoutFreshservice.description',
    url: 'https://www.freshworks.com/freshservice/migrate-freshstatus-to-new-freshservice/'
  }
];
const sanitizeDateInput = (dateString) => {
  if (typeof dateString !== "string" || dateString.length > 200) {
    throw new Error("Invalid date input");
  }
  return dateString;
};

const safeMoment = (dateString, format = "YYYY-MM-DD") => {
  try {
    const sanitizedDate = sanitizeDateInput(dateString);
    return moment(sanitizedDate, format, true); // `true` ensures strict parsing
  } catch (error) {
    console.error("Date parsing error:", error.message);
    return null;
  }
};


const FsMigrationBanner = ({ user, deprecationDate=''}) => {
  const { t } = useTranslation();
  const [isBannerModalOpen, setIsBannerModalOpen] = useState(false);
  const formattedDate = safeMoment(deprecationDate).format("MMMM D, YYYY");
  const initiateExport = async () => {
    //Close the modal after export button clicked
    setIsBannerModalOpen(false);
    try {
      const payload = {};
      const archiveResponse = await request(`/export/archive/`, {
        method: 'POST',
        payload,
        toast: {
          type: 'success',
          message: t('components.fsBanner.bannerModal.requestSubmitted')
        }
      }) || {};
      
      if (archiveResponse.status === 400) {
        const errorMessages = {
          ERR_ACC_001: 'components.fsBanner.bannerModal.requestAlreadyInitiated',
          ERR_ACC_002: 'components.fsBanner.bannerModal.maxRequestSubmitted',
          ERR_ACC_003: 'components.fsBanner.bannerModal.maxDailyRequestSubmitted'
        };
      
        const errorCode = archiveResponse.response && archiveResponse.response.error_code;
        if (errorMessages[errorCode]) {
          pushToast({
            type: 'warning',
            message: t(errorMessages[errorCode])
          });
        }
      }
    } catch (error) {
      pushToast({
        type: 'warning',
        message: t('components.fsBanner.bannerModal.requestTechnicalIssue')
      });
    }
  };

  return (
    <>
      <StyledFsMigrationBanner>
        <img className="banner-icon" src={AnnouncementLogo} alt="" />
        <p className="banner-text">
          {t('components.fsBanner.bannerText', {
            date: formattedDate
          })}
        </p>
        {user && ['OW', 'AD'].includes(user.role) && (
          <button
            type="button"
            className="banner-button-as-link"
            onClick={() => setIsBannerModalOpen(true)}
          >
            {t('components.fsBanner.bannerButton')}
          </button>
        )}
      </StyledFsMigrationBanner>
      <Confirmation
        isOpen={isBannerModalOpen}
        onDismiss={() => setIsBannerModalOpen(false)}
        cancelButtonText={t('screens.incidents.cancel')}
        contentStyles={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          borderRadius: '8px'
        }}
        footerStyles={{
          borderRadius: '0px 0px 8px 8px',
        }}
      >
        <ConfirmationDialogContent>
          <h3 className="banner-modal-header">
            {t('components.fsBanner.bannerModal.title')}
          </h3>
          <p className="banner-modal-description">
            <Trans
              i18nKey="components.fsBanner.bannerModal.description"
              values={{ date: formattedDate }}
            />
          </p>
          <div className="banner-modal-actions">
            <h5 className="banner-modal-actions-header">
              {t('components.fsBanner.bannerModal.migration.title')}
            </h5>
            <ul className="migration-links">
              {MIGRATION_LINKS.map(link => (
                <li className="migration-link-item" key={link.title}>
                  <a
                    className="migration-link"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <h5 className="migration-link-header">
                      {t(link.title)}
                    </h5>
                    <p className="migration-link-description">
                      <Trans
                        i18nKey={link.content}
                        values={{ date: formattedDate }}
                      />
                    </p>
                  </a>
                </li>
              ))}
            </ul>
            <div
              className="banner-modal-export"
              onClick={initiateExport}
            >
              <h5 className="banner-modal-actions-header">{t('components.fsBanner.bannerModal.exportButtonTitle')}</h5>
              <p className="banner-modal-description">{t('components.fsBanner.bannerModal.exportButtonDescription')}</p>
            </div>
          </div>
        </ConfirmationDialogContent>
      </Confirmation>
    </>
  );
};

export default FsMigrationBanner;
